import {
  ClipboardListIcon,
  ClockIcon,
  CodeIcon,
  CursorClickIcon,
  DocumentTextIcon,
  FilmIcon,
  GlobeAltIcon,
  PhotographIcon,
  PresentationChartBarIcon,
  PuzzleIcon,
  SearchIcon,
  ShieldCheckIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';
import React from 'react';

type Capability = {
  name: string;
  description: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

const CAPABILITIES: Capability[] = [
  {
    name: 'Builds & ships websites',
    description:
      'From prompt to a live URL — database, auth and custom domain included.',
    Icon: GlobeAltIcon,
  },
  {
    name: 'Browses the real web',
    description:
      'Drives an actual browser: logs in, clicks, fills forms — and hands you control anytime.',
    Icon: CursorClickIcon,
  },
  {
    name: 'Deep research',
    description:
      'Multi-step research across the web and academic sources, with citations.',
    Icon: SearchIcon,
  },
  {
    name: 'Runs code & charts',
    description:
      'Python, data analysis and interactive charts from your files.',
    Icon: CodeIcon,
  },
  {
    name: 'Designs presentations',
    description:
      'Full slide decks with visuals — export to PowerPoint when done.',
    Icon: PresentationChartBarIcon,
  },
  {
    name: 'Creates images & media',
    description:
      'Images, infographics and edits with the best generation models.',
    Icon: PhotographIcon,
  },
  {
    name: 'Video, music & voice',
    description:
      'Cinematic clips, full songs and lifelike voiceovers on request.',
    Icon: FilmIcon,
  },
  {
    name: 'Works with your files',
    description:
      'Reads, analyzes and edits PDFs, docs, sheets and entire folders.',
    Icon: DocumentTextIcon,
  },
  {
    name: 'Plugs into your tools',
    description:
      'Gmail, Calendar, Drive, Notion, Canva, GitHub — plus custom APIs and MCP.',
    Icon: PuzzleIcon,
  },
  {
    name: 'Scheduled tasks',
    description:
      'Recurring jobs that run on their own — briefings, reports, monitoring.',
    Icon: ClockIcon,
  },
  {
    name: 'Plans long tasks',
    description:
      'Big jobs get a visible plan, tracked phase by phase until done.',
    Icon: ClipboardListIcon,
  },
  {
    name: 'Private & secure',
    description: 'SOC 2, ISO 27001, GDPR and HIPAA-compliant infrastructure.',
    Icon: ShieldCheckIcon,
  },
];

const MarkyCapabilities = () => {
  const { t } = useLocale('marky');

  return (
    <section
      id='capabilities'
      className='relative bg-slate-50 py-20 dark:bg-slate-900 lg:py-28'
    >
      <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
        <div className='mb-12 text-center'>
          <h2 className='text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white md:text-5xl'>
            {t('One agent, endless capabilities')}
          </h2>
          <p className='mx-auto mt-4 max-w-2xl text-lg text-slate-600 dark:text-slate-400'>
            {t(
              'Everything below happens inside one chat — no tab-juggling, no copy-paste.',
            )}
          </p>
        </div>

        <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4 lg:grid-cols-3'>
          {CAPABILITIES.map((capability) => (
            <div
              key={capability.name}
              className='group relative flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-all hover:border-indigo-300 hover:shadow-md dark:border-white/10 dark:bg-white/[0.04] dark:hover:border-indigo-400/40 dark:hover:bg-white/[0.07]'
            >
              <span className='flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 ring-1 ring-inset ring-indigo-100 dark:bg-indigo-500/15 dark:text-indigo-300 dark:ring-indigo-400/20'>
                <capability.Icon className='h-5 w-5' aria-hidden='true' />
              </span>
              <span className='min-w-0'>
                <span className='block font-bold text-slate-900 dark:text-white'>
                  {t(capability.name)}
                </span>
                <span className='mt-0.5 block text-sm leading-snug text-slate-600 dark:text-slate-400'>
                  {t(capability.description)}
                </span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MarkyCapabilities;
